import React, { useRef } from 'react';
import { View, StyleSheet, Animated, I18nManager, Dimensions } from 'react-native';
import { PanGestureHandler, State as GHState, PanGestureHandlerStateChangeEvent } from 'react-native-gesture-handler';

interface Props {
  children: React.ReactNode;
  onBack?: () => void;
  enabled?: boolean;
  edgeWidth?: number; // the area from left edge that can start the gesture
  threshold?: number; // dx threshold to trigger back
}

const SwipeBackWrapper: React.FC<Props> = ({ children, onBack, enabled = true, edgeWidth = 40, threshold = 120 }) => {
  const translateX = useRef(new Animated.Value(0)).current;
  const startX = useRef<number>(0);
  const active = useRef<boolean>(false);
  const isRTL = I18nManager.isRTL;

  const setTranslate = (dx: number) => {
    // if RTL, invert direction
    const val = isRTL ? -dx : dx;
    translateX.setValue(Math.max(0, val));
  };

  const animateTo = (toValue: number, cb?: () => void) => {
    Animated.timing(translateX, {
      toValue,
      duration: 200,
      useNativeDriver: true,
    }).start(() => cb && cb());
  };

  const screenWidth = Dimensions.get('window').width;

  // use PanGestureHandler for more reliable gesture capturing
  const onGestureEvent = Animated.event(
    [{ nativeEvent: { translationX: translateX } }],
    { useNativeDriver: true }
  );

  const onHandlerStateChange = (event: PanGestureHandlerStateChangeEvent) => {
    const { nativeEvent } = event;
    if (!enabled || !onBack) return;
    if (nativeEvent.state === GHState.BEGAN) {
      startX.current = nativeEvent.x;
      active.current = true;
      translateX.setValue(0);
      console.log('[SwipeBack] began at', startX.current);
    }
    if (nativeEvent.state === GHState.ACTIVE) {
      // already handled by onGestureEvent
    }
    if (nativeEvent.state === GHState.END || nativeEvent.state === GHState.CANCELLED || nativeEvent.state === GHState.FAILED) {
      active.current = false;
      const dx = nativeEvent.translationX;
      const vx = nativeEvent.velocityX || 0;
      console.log('[SwipeBack] end dx=', dx, 'vx=', vx);
      const effectiveThreshold = Math.max(threshold, screenWidth * 0.22);
      if (dx > effectiveThreshold || vx > 1000) {
        const offscreen = screenWidth + 200;
        animateTo(offscreen, () => {
          translateX.setValue(0);
          onBack && onBack();
        });
      } else {
        animateTo(0);
      }
    }
  };

  return (
    <View style={styles.container}>
      <PanGestureHandler
        onGestureEvent={onGestureEvent}
        onHandlerStateChange={onHandlerStateChange}
        enabled={enabled}
        activeOffsetX={[0, 20]}
        failOffsetY={[-20, 20]}
        hitSlop={{ left: 0, width: 60 }}
        minPointers={1}
      >
        <Animated.View style={{ flex: 1, transform: [{ translateX }] }}>
          {children}
        </Animated.View>
      </PanGestureHandler>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default SwipeBackWrapper;
